import React, { useMemo, useId, useState, useRef, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Tooltip } from "antd";
import EquifaxLogo from "_assets/logos/equifax_small.svg";
import "./CreditScorePanel.scss";

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

// Geometry constants for the gauge (matches designer spec)
const CENTER_X = 117.5;
const CENTER_Y = 130;
const RADIUS_OUTER = 118;
const RADIUS_INNER = 95.42;
const RADIUS_INDICATOR = (RADIUS_OUTER + RADIUS_INNER) / 2;
const MIN_SCORE = 300;
const MAX_SCORE = 850;
const SCORE_RANGE = MAX_SCORE - MIN_SCORE;
const INCREMENT = 20;

// The SVG path for the gauge arc (from designer's Figma export)
const GAUGE_PATH =
  "M224.205 130C230.438 130 235.548 124.934 234.953 118.729C232.348 91.5697 220.388 66.018 200.933 46.5614C178.805 24.4321 148.793 12 117.5 12C86.2066 12 56.1949 24.4321 34.0671 46.5614C14.6118 66.018 2.65228 91.5697 0.047252 118.729C-0.547983 124.934 4.56153 130 10.7953 130C17.029 130 22.0134 124.928 22.749 118.738C25.2632 97.5779 34.8103 77.7453 50.0296 62.525C67.9239 44.6295 92.1937 34.5759 117.5 34.5759C142.806 34.5759 167.076 44.6295 184.97 62.525C200.19 77.7453 209.737 97.5779 212.251 118.738C212.987 124.928 217.971 130 224.205 130Z";

// Audio context singleton for click sounds
let audioContextRef = null;

function initAudio() {
  if (typeof window === "undefined") return;

  if (!audioContextRef) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      audioContextRef = new AudioContextClass();
    }
  }

  if (audioContextRef && audioContextRef.state === "suspended") {
    audioContextRef.resume().catch(() => {});
  }
}

function triggerFeedback() {
  if (typeof window === "undefined") return;

  // Haptic feedback for mobile
  if (navigator?.vibrate) {
    navigator.vibrate(5);
  }

  // Audio click feedback
  initAudio();
  const ctx = audioContextRef;
  if (!ctx) return;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.connect(gain);
  gain.connect(ctx.destination);

  // Create a short, crisp "tick" sound
  osc.type = "sine";
  osc.frequency.setValueAtTime(800, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.03);

  gain.gain.setValueAtTime(0.1, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.03);

  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.03);
}

// Helper to convert polar coordinates to cartesian
function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

// Helper to create an SVG path for a donut segment (for hit areas)
function describeArc(x, y, innerRadius, outerRadius, startAngle, endAngle) {
  const start = polarToCartesian(x, y, outerRadius, endAngle);
  const end = polarToCartesian(x, y, outerRadius, startAngle);
  const start2 = polarToCartesian(x, y, innerRadius, endAngle);
  const end2 = polarToCartesian(x, y, innerRadius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  const d = [
    "M", start.x, start.y,
    "A", outerRadius, outerRadius, 0, largeArcFlag, 0, end.x, end.y,
    "L", end2.x, end2.y,
    "A", innerRadius, innerRadius, 0, largeArcFlag, 1, start2.x, start2.y,
    "Z"
  ].join(" ");

  return d;
}

function CreditScorePanel({ report, onOpenDrawer }) {
  // Generate unique IDs for SVG elements to avoid conflicts
  const id = useId();
  const gradientId = `gradient-${id}`;
  const filterGaugeId = `filter-gauge-${id}`;
  const filterIndicatorId = `filter-indicator-${id}`;
  const svgRef = useRef(null);

  // Interactive state
  const [hoveredScore, setHoveredScore] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  // Extract score from credit report models array
  const actualScore = report?.models?.[0]?.score || null;
  const reportDate = report?.createdAt;

  // Display score: show hovered score when interacting, otherwise show actual score
  const displayScore = hoveredScore !== null ? hoveredScore : actualScore;

  // Generate interactive segments for hover detection
  const segments = useMemo(() => {
    const segs = [];
    for (let score = MIN_SCORE; score < MAX_SCORE; score += INCREMENT) {
      const nextScore = Math.min(score + INCREMENT, MAX_SCORE);
      const midScore = Math.round((score + nextScore) / 2);

      const startAngle = 180 + ((score - MIN_SCORE) / SCORE_RANGE) * 180;
      const endAngle = 180 + ((nextScore - MIN_SCORE) / SCORE_RANGE) * 180;

      // Extend the hit area to include the tick marks (larger inner radius reduction)
      segs.push({
        path: describeArc(CENTER_X, CENTER_Y, RADIUS_INNER - 25, RADIUS_OUTER, startAngle, endAngle),
        range: `${score} - ${nextScore}`,
        midScore,
        startScore: score,
        endScore: nextScore,
        key: `seg-${score}`
      });
    }
    return segs;
  }, []);

  // Calculate indicator position based on display score
  const indicatorPos = useMemo(() => {
    if (!displayScore) return { x: CENTER_X, y: CENTER_Y - RADIUS_INDICATOR };

    // Clamp score to valid range
    const clampedScore = Math.max(MIN_SCORE, Math.min(MAX_SCORE, displayScore));

    // Calculate angle: 180 degrees (left) to 360 degrees (right)
    const indicatorAngle =
      180 + ((clampedScore - MIN_SCORE) / SCORE_RANGE) * 180;
    return polarToCartesian(
      CENTER_X,
      CENTER_Y,
      RADIUS_INDICATOR,
      indicatorAngle
    );
  }, [displayScore]);

  // Generate tick marks
  const ticks = useMemo(() => {
    const t = [];
    const numTicks = 45;
    const tickRadiusOuter = RADIUS_INNER - 8;
    const tickRadiusInner = RADIUS_INNER - 18;

    for (let i = 0; i <= numTicks; i++) {
      const angle = 180 + (i / numTicks) * 180;
      const start = polarToCartesian(
        CENTER_X,
        CENTER_Y,
        tickRadiusInner,
        angle
      );
      const end = polarToCartesian(CENTER_X, CENTER_Y, tickRadiusOuter, angle);
      t.push(
        <line
          key={`tick-${i}`}
          x1={start.x}
          y1={start.y}
          x2={end.x}
          y2={end.y}
          stroke="#E5E7EB"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      );
    }
    return t;
  }, []);

  // Play tick sound and vibrate when hovered score changes
  useEffect(() => {
    if (hoveredScore !== null) {
      triggerFeedback();
    }
  }, [hoveredScore]);

  // Handle touch/mouse interaction for mobile
  const handleInteraction = useCallback((e) => {
    initAudio(); // Ensure audio context is ready on first interaction

    if (!svgRef.current) return;

    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const rect = svgRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Scale coordinates if SVG is scaled
    const scaleX = 235 / rect.width;
    const scaleY = 130 / rect.height;

    const svgX = x * scaleX;
    const svgY = y * scaleY;

    // Calculate angle from center
    const dx = svgX - CENTER_X;
    const dy = svgY - CENTER_Y;

    let angleRad = Math.atan2(dy, dx);
    let angleDeg = angleRad * (180 / Math.PI);

    // Convert to 0-360 range
    let gaugeAngle = angleDeg;
    if (angleDeg < 0) {
      gaugeAngle = angleDeg + 360;
    }

    // Clamp to valid gauge range (180-360)
    if (dy > 0) {
      // Below center - clamp to nearest edge
      if (dx > 0) gaugeAngle = 360;
      else gaugeAngle = 180;
    } else {
      if (gaugeAngle < 180) gaugeAngle = 180;
      if (gaugeAngle > 360) gaugeAngle = 360;
    }

    // Map angle to score
    const rawScore = MIN_SCORE + ((gaugeAngle - 180) / 180) * SCORE_RANGE;

    // Find closest segment
    const index = Math.floor((rawScore - MIN_SCORE) / INCREMENT);
    const safeIndex = Math.max(0, Math.min(index, segments.length - 1));
    const segment = segments[safeIndex];

    setHoveredScore(segment.midScore);
    setTooltipVisible(true);
  }, [segments]);

  const handleMouseLeave = useCallback(() => {
    setHoveredScore(null);
    setTooltipVisible(false);
  }, []);

  const handleSegmentEnter = useCallback((segment) => {
    initAudio();
    setHoveredScore(segment.midScore);
    setTooltipVisible(true);
  }, []);

  // Format date as relative time
  const getRelativeDate = () => {
    if (!reportDate) return null;
    const date = dayjs(parseInt(reportDate));
    return `Updated ${date.fromNow()}`;
  };

  return (
    <div className="credit-score-panel">
      {/* Header */}
      <div className="panel-header">
        <h2 className="panel-title">Credit Score</h2>
        <button className="credit-report-link" onClick={onOpenDrawer}>
          <div className="report-icon">
            <img src={EquifaxLogo} alt="Equifax" />
          </div>
          <span>Full Credit Report</span>
        </button>
      </div>

      {actualScore ? (
        <div className="gauge-section">
          <div className="gauge-wrapper">
            {/* SVG Gauge */}
            <div className="gauge-container">
              <svg
                ref={svgRef}
                className="gauge-svg"
                fill="none"
                preserveAspectRatio="xMidYMid meet"
                viewBox="0 0 235 130"
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleInteraction}
                onTouchMove={handleInteraction}
                onTouchEnd={handleMouseLeave}
              >
                {/* Defs for filters and gradients - must be first */}
                <defs>
                  {/* Inner shadow filter for gauge arc */}
                  <filter
                    id={filterGaugeId}
                    x="0"
                    y="12"
                    width="235"
                    height="119"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feBlend
                      in="SourceGraphic"
                      in2="BackgroundImageFix"
                      mode="normal"
                      result="shape"
                    />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dy="1" />
                    <feGaussianBlur stdDeviation="4" />
                    <feComposite
                      in2="hardAlpha"
                      operator="arithmetic"
                      k2="-1"
                      k3="1"
                    />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.16 0"
                    />
                    <feBlend
                      in2="shape"
                      mode="normal"
                      result="effect1_innerShadow"
                    />
                  </filter>

                  {/* Drop shadow filter for indicator */}
                  <filter
                    id={filterIndicatorId}
                    x="-10"
                    y="-9"
                    width="20"
                    height="20"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dy="1" />
                    <feGaussianBlur stdDeviation="1" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                    />
                    <feBlend
                      in2="BackgroundImageFix"
                      mode="normal"
                      result="effect1_dropShadow"
                    />
                    <feBlend
                      in="SourceGraphic"
                      in2="effect1_dropShadow"
                      mode="normal"
                      result="shape"
                    />
                  </filter>

                  {/* Gradient: green (right) → yellow (middle) → red (left) */}
                  <linearGradient
                    id={gradientId}
                    x1="235.492"
                    y1="130"
                    x2="-0.491991"
                    y2="130"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#A3E635" />
                    <stop offset="0.495192" stopColor="#FCD34D" />
                    <stop offset="1" stopColor="#E11D48" />
                  </linearGradient>
                </defs>

                {/* Tick marks - rendered first (behind gauge) */}
                <g>{ticks}</g>

                {/* Gauge arc with gradient and inner shadow */}
                <g filter={`url(#${filterGaugeId})`}>
                  <path d={GAUGE_PATH} fill={`url(#${gradientId})`} />
                </g>

                {/* Interactive segment overlays (invisible hit targets) */}
                <g className="gauge-segments">
                  {segments.map((seg) => (
                    <Tooltip
                      key={seg.key}
                      title={seg.range}
                      open={hoveredScore === seg.midScore && tooltipVisible}
                      placement="top"
                      overlayClassName="gauge-tooltip"
                    >
                      <path
                        d={seg.path}
                        fill="transparent"
                        className="gauge-segment"
                        onMouseEnter={() => handleSegmentEnter(seg)}
                        onMouseMove={() => handleSegmentEnter(seg)}
                      />
                    </Tooltip>
                  ))}
                </g>

                {/* Indicator dot with drop shadow */}
                <g
                  filter={`url(#${filterIndicatorId})`}
                  className="gauge-indicator"
                  style={{
                    transform: `translate(${indicatorPos.x}px, ${indicatorPos.y}px)`,
                  }}
                >
                  <circle cx="0" cy="0" r="8" fill="white" />
                  <circle
                    cx="0"
                    cy="0"
                    r="7.75"
                    stroke="#A8A29E"
                    strokeWidth="0.5"
                    fill="none"
                  />
                </g>
              </svg>
            </div>

            {/* Score and date - positioned to overlap the gauge */}
            <div className="score-overlay">
              <span className={`score-number ${hoveredScore !== null ? "hovering" : ""}`}>
                {displayScore}
              </span>
              {getRelativeDate() && hoveredScore === null && (
                <span className="score-date">{getRelativeDate()}</span>
              )}
              {hoveredScore !== null && (
                <span className="score-date hovering">Score Range</span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="score-section">
          <div className="no-score-message">No credit score available</div>
        </div>
      )}
    </div>
  );
}

export default CreditScorePanel;
